import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

/**
 * Cross-component handle on the offer→bookings ingest workflow. The
 * "Build bookings" button on the ServiceOffers list calls
 * `startIngest(offer.Id)`; the ChatWidget subscribes and renders its
 * near-fullscreen ingest overlay whenever `activeOfferId` is set.
 *
 * Decoupling via Context (instead of URL routing) keeps the operator
 * on the offers page in the background — the overlay sits over it
 * with a dim backdrop, and closing the overlay returns them to
 * exactly where they were.
 */

const IngestContext = createContext({
  activeOfferId: null,
  startIngest: () => {},
  endIngest: () => {},
});

export function IngestProvider({ children }) {
  const [activeOfferId, setActiveOfferId] = useState(null);

  const startIngest = useCallback((offerId) => {
    if (!offerId) return;
    setActiveOfferId(offerId);
  }, []);

  const endIngest = useCallback(() => {
    setActiveOfferId(null);
  }, []);

  const value = useMemo(() => ({
    activeOfferId,
    startIngest,
    endIngest,
  }), [activeOfferId, startIngest, endIngest]);

  return (
    <IngestContext.Provider value={value}>
      {children}
    </IngestContext.Provider>
  );
}

export function useIngestController() {
  return useContext(IngestContext);
}

export default IngestContext;
